import React from "react";
import { X, FileSpreadsheet } from "lucide-react";
import { EntityKind } from "../../Dashboard/types";
import EntityIcon from "../../Dashboard/Components/EntityIcon";
import { entityKindMap } from "../../utils";
import { useEntityTypeVisibility } from "../../Data";
import { Tooltip } from "@mui/material";
import { useCanEditRms } from "@/hooks/useCanEditRms";

interface EntityTypeSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onEntityTypeSelect: (entityType: EntityKind) => void;
  onImportSpreadsheet?: () => void;
  excludeTypes?: EntityKind[];
}

const EntityTypeSelector: React.FC<EntityTypeSelectorProps> = ({
  isOpen,
  onClose,
  onEntityTypeSelect,
  onImportSpreadsheet,
  excludeTypes = [],
}) => {
  const canEditRms = useCanEditRms();

  // Check which entity types have facts defined
  const { shouldShowEntity, isLoading: isLoadingVisibility } =
    useEntityTypeVisibility();

  // Get entity types from entityKindMap, excluding specified types and those without facts
  // Only show entities once loading is complete
  const entityTypes = Object.values(entityKindMap).filter(
    (entityType) =>
      !excludeTypes.includes(entityType.key) && shouldShowEntity(entityType.key)
  );

  if (!isOpen) return null;

  return (
    <div
      // A dialog by role, so a keyboard handler further up the tree (the
      // Facts grid's close-on-Escape, #11880) can tell keys typed in here
      // from keys typed in the row that opened it.
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b pb-4 mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">Create</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {isLoadingVisibility ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                className="flex items-center p-4 border border-gray-200 rounded-lg animate-pulse"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gray-200 rounded-lg mr-4" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-32 bg-gray-200 rounded" />
                  <div className="h-4 w-48 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {entityTypes.map((entityType) => {
              const isInteraction = entityType.key === "interaction";
              const disabled = !canEditRms && !isInteraction;
              return (
                <Tooltip
                  key={entityType.key}
                  title={
                    disabled
                      ? "Your team does not have permission to edit RMS data"
                      : ""
                  }
                >
                  <span>
                    <button
                      onClick={() => onEntityTypeSelect(entityType.key)}
                      disabled={disabled}
                      className={`flex items-center p-4 border border-gray-200 rounded-lg transition-colors text-left w-full ${
                        disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                        <EntityIcon
                          kind={entityType.key}
                          size={24}
                          className="text-gray-600"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900">
                          {entityType.label}
                        </h3>
                      </div>
                    </button>
                  </span>
                </Tooltip>
              );
            })}
            {onImportSpreadsheet && canEditRms && (
              <Tooltip title="">
                <span>
                  <button
                    onClick={onImportSpreadsheet}
                    className="flex items-center p-4 border border-gray-200 rounded-lg transition-colors text-left w-full hover:border-gray-300 hover:bg-gray-50"
                  >
                    <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                      <FileSpreadsheet size={24} className="text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        Import from Spreadsheet
                      </h3>
                    </div>
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityTypeSelector;
